import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { enhancedApi } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { 
  Save, 
  Send, 
  Plus, 
  Trash2, 
  FileText,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  Shield,
  Activity,
  Heart,
  Stethoscope,
  Clock,
  Users,
  Target,
  TrendingUp,
  MessageSquare,
  UserCheck,
  Eye,
  Edit3,
  CheckCircle2,
  XCircle,
  BarChart3
} from 'lucide-react';
// Custom signature canvas component to avoid re-rendering issues
const CustomSignatureCanvas = React.forwardRef<HTMLCanvasElement, {
  onBegin?: () => void;
  onEnd?: () => void;
  width?: number;
  height?: number;
  className?: string;
  defaultSignature?: string;
}>(({ onBegin, onEnd, width = 400, height = 150, className = '', defaultSignature }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Combine refs
  React.useImperativeHandle(ref, () => canvasRef.current!);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    isDrawingRef.current = true;
    onBegin?.();
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e as any).clientX ? (e as any).clientX - rect.left : (e as any).touches[0].clientX - rect.left;
    const y = (e as any).clientY ? (e as any).clientY - rect.top : (e as any).touches[0].clientY - rect.top;

    lastPointRef.current = { x, y };
  }, [onBegin]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Configure the context for drawing
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000000';

    const rect = canvas.getBoundingClientRect();
    const x = (e as any).clientX ? (e as any).clientX - rect.left : (e as any).touches[0].clientX - rect.left;
    const y = (e as any).clientY ? (e as any).clientY - rect.top : (e as any).touches[0].clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current?.x || x, lastPointRef.current?.y || y);
    ctx.lineTo(x, y);
    ctx.stroke();

    lastPointRef.current = { x, y };
  }, []);

  const stopDrawing = useCallback(() => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
    onEnd?.();
  }, [onEnd]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const toDataURL = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    return canvas.toDataURL();
  }, []);

  const isEmpty = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return imageData.data.every(pixel => pixel === 0);
  }, []);

  const fromDataURL = useCallback((dataURL: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = dataURL;
  }, []);

  // Initialize canvas context and expose methods
  React.useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Configure the context for drawing
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#000000';
      }
      
      (canvas as any).clear = clear;
      (canvas as any).getSignatureDataURL = toDataURL;
      (canvas as any).isEmpty = isEmpty;
      (canvas as any).fromDataURL = fromDataURL;
    }
  }, [clear, toDataURL, isEmpty, fromDataURL]);

  // Reload signature when defaultSignature changes
  React.useEffect(() => {
    if (defaultSignature && canvasRef.current) {
      fromDataURL(defaultSignature);
    }
  }, [defaultSignature, fromDataURL]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`border rounded ${className}`}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
      style={{ touchAction: 'none' }}
    />
  );
});

interface AuditFormData {
  // Case Information
  patient_ip_number: string;
  age: string;
  age_unit: string;
  admission_date: string;
  discharge_date: string;
  length_of_stay: number;
  source_of_admission: string;
  primary_diagnosis: string;
  previous_admission_30_days: boolean | string;
  dnr_status: string;
  cause_of_death: string;
  autopsy_needed: boolean | string;
  autopsy_justification: string;
  rca_needed: boolean | string;
  rca_comment: string;
  cpr_concern: boolean | string;
  cpr_comment: string;
  mortality_unexpected: boolean | string;
  mortality_unexpected_comment: string;
  mortality_not_expected_admission: boolean | string;
  mortality_not_expected_admission_comment: string;
  
  // Case Summary
  case_summary: string;
  
  // Structured Questions
  documentation_questions: QuestionResponse[];
  diagnosis_questions: QuestionResponse[];
  management_questions: QuestionResponse[];
  outcome_questions: QuestionResponse[];
  disclosure_questions: QuestionResponse[];
  second_victim_questions: QuestionResponse[];
  
  // Issues and Recommendations
  issues_revealed: string;
  recommendations: Recommendation[];
  
  // Audit Team
  audit_team: TeamMember[];
}

interface QuestionResponse {
  id: string;
  question: string;
  answer: string;
  comment: string;
}

interface Recommendation {
  id?: number;
  title: string;
  description: string;
  category_id: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assigned_to?: number;
  due_date: string;
  action_plan: string;
  lead_person: string;
}

interface TeamMember {
  name: string;
  role: string;
  department: string;
  signature: string;
  signature_date: string;
}


interface PatientData {
  id: number;
  patient_id: string;
  note_no?: string;
  time_of_death?: string;
  cause_of_death?: string;
  final_diagnosis?: string;
  dept?: string;
  unit_id?: string;
  unit_name?: string;
  age?: string;
  gender?: string;
  admission_date?: string;
  discharge_date?: string;
  primary_diagnosis?: string;
  case_summary?: string;
  case_type: 'mortality' | 'morbidity';
}

interface RecommendationCategory {
  id: number;
  name: string;
  description: string;
  color: string;
}

interface FormSection {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  completed: boolean;
  required: boolean;
}

interface AutoSaveState {
  lastSaved: Date | null;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
}

const questionSets = {
  documentation: [
    { id: 'doc_001', question: 'Was the documentation adequate (Clear, Continuous, clear plan )?', options: ['Yes', 'No', 'NA'] },
    { id: 'doc_002', question: 'Were clinical findings properly recorded?', options: ['Yes', 'No', 'NA'] },
    { id: 'doc_003', question: 'Was the discharge plan documented?', options: ['Yes', 'No', 'NA'] },
    { id: 'doc_004', question: 'Was CPR process documented in the system?', options: ['Yes', 'No', 'NA'] },
    { id: 'doc_005', question: 'Was discussion with the family regarding resuscitative measures documented?', options: ['Yes', 'No', 'NA'] },
    { id: 'doc_006', question: 'Was the death note by a physician documented in Al-Shifa?', options: ['Yes', 'No', 'NA'] }
  ],
  diagnosis: [
    { id: 'diag_001', question: 'WWas there a delay in diagnosis/assessment?', options: ['Yes', 'No', 'NA'] },
    { id: 'diag_002', question: 'Were abnormal lab, x-ray, other test results or physical findings addressed?', options: ['Yes', 'No', 'NA'] },
    { id: 'diag_003', question: 'Was there any incorrect information provided or Misinterpretation of information?', options: ['Yes', 'No', 'NA'] }
  ],
  management: [
    { id: 'mgmt_001', question: 'Was there a delay in initiating treatment?', options: ['Yes', 'No', 'NA'] },
    { id: 'mgmt_002', question: 'Did the care management deviate from the policy or Clinical Practice Guidelines (if available) / Current International Practice?', options: ['Yes', 'No', 'NA'] },
    { id: 'mgmt_003', question: 'Was the deterioration in the patient recognized and responded in a timely manner?', options: ['Yes', 'No', 'NA'] },
    { id: 'mgmt_004', question: 'Was assistance available when required?', options: ['Yes', 'No', 'NA'] },
    { id: 'mgmt_005', question: 'Was the skill-mix available appropriate?', options: ['Yes', 'No', 'NA'] }
  ],
  outcome: [
    { id: 'outcome_001', question: 'Was there a medication error which may have contributed to the outcome?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_002', question: 'Did teamwork failure (communication) contribute to the outcome?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_003', question: 'Were there any local environmental contributors?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_004', question: 'Were there any hospital-wide contributors?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_005', question: 'Was there a complication due to Treatment/procedure/operation?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_006', question: 'Under optimal conditions would this mortality (or morbidity) have been preventable?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_007', question: 'Was the outcome of the treatment expected?', options: ['Yes', 'No', 'NA'] },
    { id: 'outcome_008', question: 'Is further review necessary?', options: ['Yes', 'No', 'NA'] }
  ],
  disclosure: [
    { id: 'disc_001', question: 'Was the Disclosure session conducted in private?', options: ['Yes', 'No', 'NA'] },
    { id: 'disc_002', question: 'Was the Adverse Event disclosure conducted with a guidance of the Checklist?', options: ['Yes', 'No', 'NA'] },
    { id: 'disc_003', question: 'Was the Event Disclosure Led By consultant?', options: ['Yes', 'No', 'NA'] },
    { id: 'disc_004', question: 'Supervised By (HOD/Director)?', options: ['Yes', 'No', 'NA'] }
  ],
  second_victim: [
    { id: 'victim_001', question: 'Was a Second Victim identified?', options: ['Yes', 'No', 'NA'] },
    { id: 'victim_002', question: 'Was the Second Victim support team notified?', options: ['Yes', 'No', 'NA'] }
  ]
};

export const AuditForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const signatureRefs = useRef<{ [key: number]: HTMLCanvasElement | null }>({});
  const isDrawingRef = useRef<{ [key: number]: boolean }>({});
  const signaturesLoadedRef = useRef<boolean>(false);
  const signatureLoadingDisabledRef = useRef<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [systemSettings, setSystemSettings] = useState<any>({});
  const [userRole, setUserRole] = useState<string>('');
  
  // New state for enhanced features
  const [currentSection, setCurrentSection] = useState(0);
  const [recommendationCategories, setRecommendationCategories] = useState<RecommendationCategory[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>({
    lastSaved: null,
    isSaving: false,
    hasUnsavedChanges: false
  });
  const [formProgress, setFormProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [sectionCompletion, setSectionCompletion] = useState<{[key: string]: boolean}>({});
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Refs for each section to enable scroll-to-section functionality
  const caseInfoRef = useRef<HTMLDivElement>(null);
  const caseSummaryRef = useRef<HTMLDivElement>(null);
  const documentationRef = useRef<HTMLDivElement>(null);
  const diagnosisRef = useRef<HTMLDivElement>(null);
  const managementRef = useRef<HTMLDivElement>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);
  const disclosureRef = useRef<HTMLDivElement>(null);
  const secondVictimRef = useRef<HTMLDivElement>(null);
  const issuesRef = useRef<HTMLDivElement>(null);
  const recommendationsRef = useRef<HTMLDivElement>(null);
  const teamRef = useRef<HTMLDivElement>(null);
  const validationErrorsRef = useRef<HTMLDivElement>(null);
  
  const sectionRefs = [
    caseInfoRef,
    caseSummaryRef,
    documentationRef,
    diagnosisRef,
    managementRef,
    outcomeRef,
    disclosureRef,
    secondVictimRef,
    issuesRef,
    recommendationsRef,
    teamRef
  ];
  
  // Function to scroll to a specific section
  const scrollToSection = useCallback((sectionIndex: number) => {
    const ref = sectionRefs[sectionIndex];
    
    if (ref?.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    } else {
      console.warn(`Section ref ${sectionIndex} not found`);
    }
  }, []);

  // Function to scroll to validation errors
  const scrollToValidationErrors = useCallback(() => {
    if (validationErrorsRef.current) {
      validationErrorsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }, []);

  const { register, control, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<AuditFormData>({
    defaultValues: {
      // Boolean fields with default values
      previous_admission_30_days: false,
      autopsy_needed: false,
      rca_needed: false,
      cpr_concern: false,
      mortality_unexpected: false,
      mortality_not_expected_admission: false,
      // Structured questions
      documentation_questions: questionSets.documentation.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      diagnosis_questions: questionSets.diagnosis.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      management_questions: questionSets.management.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      outcome_questions: questionSets.outcome.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      disclosure_questions: questionSets.disclosure.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      second_victim_questions: questionSets.second_victim.map(q => ({ id: q.id, question: q.question, answer: '', comment: '' })),
      recommendations: [],
      audit_team: []
    }
  });

  const { fields: recommendationFields, append: appendRecommendation, remove: removeRecommendation } = useFieldArray({
    control,
    name: 'recommendations'
  });

  const { fields: teamFields, append: appendTeamMember, remove: removeTeamMember } = useFieldArray({
    control,
    name: 'audit_team'
  });

  // Form sections configuration
  const formSections: FormSection[] = [
    { id: 'case-info', title: 'Case Information', icon: FileText, completed: false, required: true },
    { id: 'case-summary', title: 'Case Summary', icon: MessageSquare, completed: false, required: true },
    { id: 'documentation', title: 'Documentation', icon: Edit3, completed: false, required: true },
    { id: 'diagnosis', title: 'Diagnosis', icon: Eye, completed: false, required: true },
    { id: 'management', title: 'Management', icon: Activity, completed: false, required: true },
    { id: 'outcome', title: 'Outcome', icon: Target, completed: false, required: true },
    { id: 'disclosure', title: 'Disclosure', icon: UserCheck, completed: false, required: true },
    { id: 'second-victim', title: 'Second Victim', icon: Heart, completed: false, required: true },
    { id: 'issues', title: 'Issues Revealed', icon: AlertCircle, completed: false, required: false },
    { id: 'recommendations', title: 'Recommendations', icon: TrendingUp, completed: false, required: true },
    { id: 'team', title: 'Audit Team', icon: Users, completed: false, required: true }
  ];

  // Enhanced auto-save functionality with debouncing
  const autoSave = useCallback(async (data: AuditFormData) => {
    if (autoSaveState.isSaving) return;
    
    setAutoSaveState(prev => ({ ...prev, isSaving: true }));
    
    try {
      const response = await enhancedApi.audit.saveDraft(id!, data);
      if (response.success && 'data' in response) {
        setAutoSaveState({
          lastSaved: new Date(),
          isSaving: false,
          hasUnsavedChanges: false
        });
        
        // Update progress from server response
        if (response.data.progress_percentage !== undefined) {
          setFormProgress(response.data.progress_percentage);
        }
      } else {
        setAutoSaveState(prev => ({ ...prev, isSaving: false }));
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveState(prev => ({ ...prev, isSaving: false }));
    }
  }, [id, autoSaveState.isSaving]);

  // Enhanced form watching with debouncing - TEMPORARILY DISABLED
  const watchedData = watch();
  
  // TODO: Re-enable auto-save with proper debouncing
  // useEffect(() => {
  //   // Auto-save logic here
  // }, [watchedData, autoSave]);

  // Enhanced section completion validation
  const validateSectionCompletion = useCallback((data: AuditFormData) => {
    const completion: {[key: string]: boolean} = {};
    
    // Case Information
    completion['case-info'] = !!(data.patient_ip_number && data.age && data.age_unit && data.primary_diagnosis && data.cause_of_death);
    
    // Case Summary
    completion['case-summary'] = !!data.case_summary;
    
    // Documentation Questions
    completion['documentation'] = data.documentation_questions?.every(q => q.answer) || false;
    
    // Diagnosis Questions
    completion['diagnosis'] = data.diagnosis_questions?.every(q => q.answer) || false;
    
    // Management Questions
    completion['management'] = data.management_questions?.every(q => q.answer) || false;
    
    // Outcome Questions
    completion['outcome'] = data.outcome_questions?.every(q => q.answer) || false;
    
    // Disclosure Questions
    completion['disclosure'] = data.disclosure_questions?.every(q => q.answer) || false;
    
    // Second Victim Questions
    completion['second-victim'] = data.second_victim_questions?.every(q => q.answer) || false;
    
    // Issues Revealed
    completion['issues'] = !!data.issues_revealed;
    
    // Recommendations
    completion['recommendations'] = data.recommendations && data.recommendations.length > 0;
    
    // Audit Team
    completion['team'] = data.audit_team && data.audit_team.length > 0;
    
    return completion;
  }, []);

  // Calculate form progress
  const calculateProgress = useCallback((data: AuditFormData) => {
    const completion = validateSectionCompletion(data);
    const totalSections = formSections.length;
    const completedSections = Object.values(completion).filter(Boolean).length;
    return Math.round((completedSections / totalSections) * 100);
  }, [formSections, validateSectionCompletion]);

  // Update section completion when form data changes
  const prevCompletionRef = useRef<string>('');
  
  useEffect(() => {
    const currentDataString = JSON.stringify(watchedData);
    
    // Only proceed if data has actually changed
    if (currentDataString === prevCompletionRef.current) {
      return;
    }
    
    prevCompletionRef.current = currentDataString;
    
    const completion = validateSectionCompletion(watchedData);
    setSectionCompletion(completion);
    
    const progress = calculateProgress(watchedData);
    setFormProgress(progress);
  }, [watchedData]);

  useEffect(() => {
    if (id) {
      fetchAuditData();
      fetchSystemSettings();
      fetchUserRole();
    }
  }, [id]);

  // Load existing signatures when signatureRefs are available and form data is loaded
  const loadSignatures = useCallback(() => {
    // Completely disable signature loading if already loaded or disabled
    if (signaturesLoadedRef.current || signatureLoadingDisabledRef.current) {
      return;
    }

    // Check if any user is currently drawing
    const anyUserDrawing = Object.values(isDrawingRef.current).some(drawing => drawing);
    if (anyUserDrawing) {
      return;
    }

    const auditTeam = watch('audit_team');
    if (auditTeam && Array.isArray(auditTeam) && Object.keys(signatureRefs.current).length > 0) {
      auditTeam.forEach((member: any, index: number) => {
        if (member?.signature && signatureRefs.current[index]) {
          // Only load signature if the canvas is empty AND user is not currently drawing
          const isEmpty = (signatureRefs.current[index] as any)?.isEmpty();
          const isCurrentlyDrawing = isDrawingRef.current[index] || false;
          
          if (isEmpty && !isCurrentlyDrawing) {
            try {
              (signatureRefs.current[index] as any)?.fromDataURL(member.signature);
            } catch (error) {
              console.error(`Failed to load signature for team member ${index}:`, error);
            }
          }
        }
      });
      
      // Mark signatures as loaded to prevent future overwrites
      signaturesLoadedRef.current = true;
      signatureLoadingDisabledRef.current = true;
    }
  }, []); // Remove watch dependency to prevent recreation

  // Load signatures only when form data is initially loaded
  useEffect(() => {
    if (patientData && !signaturesLoadedRef.current) {
      // Small delay to ensure signature canvases are rendered
      setTimeout(() => {
        loadSignatures();
      }, 200);
    }
  }, [patientData, loadSignatures]);


  // Function to handle signature drawing start
  const handleSignatureStart = useCallback((index: number) => {
    isDrawingRef.current[index] = true;
    // Disable signature loading completely once user starts drawing
    signatureLoadingDisabledRef.current = true;
  }, []);

  // Function to handle signature drawing end
  const handleSignatureEnd = useCallback((index: number) => {
    isDrawingRef.current[index] = false;
    
    // Persist signature to form state immediately
    const signatureDataURL = (signatureRefs.current[index] as any)?.getSignatureDataURL();
    if (signatureDataURL) {
      setValue(`audit_team.${index}.signature`, signatureDataURL, { shouldDirty: true });
    }
  }, [setValue]);

  // Auto-scroll to validation errors when they appear
  useEffect(() => {
    if (validationErrors.length > 0) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        scrollToValidationErrors();
      }, 100);
    }
  }, [validationErrors.length, scrollToValidationErrors]);

  const fetchSystemSettings = async () => {
    try {
      const response = await fetch('/api/admin.php?action=system_settings');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSystemSettings(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await fetch('/api/auth.php?action=get_user_info');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUserRole(data.user.role);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user role:', error);
    }
  };

  // Check if editing is allowed for approved audits
  const canEditApprovedAudit = () => {
    if (!isViewOnly) return true;
    
    const allowEditApproved = systemSettings.allow_edit_approved_audits?.value === 'true';
    const hasEditPermission = userRole === 'reviewer' || userRole === 'superuser';
    
    return allowEditApproved && hasEditPermission;
  };

  const fetchAuditData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch audit data
      const auditResponse = await enhancedApi.audit.getAudit(id!);
      if (auditResponse.success && 'data' in auditResponse) {
        const auditData = auditResponse.data;
        setPatientData(auditData.patient);
        
        // Check if audit is approved - if so, set view-only mode
        if (auditData.audit && auditData.audit.status === 'approved') {
          setIsViewOnly(true);
        }
        
        // Set form values
        if (auditData.form) {
          Object.keys(auditData.form).forEach(key => {
            const value = auditData.form[key];
            setValue(key as keyof AuditFormData, value);
          });
          
          // Signatures will be loaded by the useEffect when signatureRefs are ready
        } else {
          // Auto-populate form fields from patient data if no existing form
          if (auditData.patient) {
            const patient = auditData.patient;
            
            // Auto-populate common fields
            setValue('patient_ip_number', patient.patient_id || '');
            if (patient.age) {
              // Parse age string (e.g., "25 years") to extract number and unit
              const ageMatch = patient.age.match(/^(\d+)\s+(hours|days|months|years)$/);
              if (ageMatch) {
                setValue('age', ageMatch[1]);
                setValue('age_unit', ageMatch[2]);
              } else {
                // Fallback: assume years if format is unexpected
                setValue('age', patient.age);
                setValue('age_unit', 'years');
              }
            }
            
            // Auto-populate admission and discharge dates
            console.log('Patient data for auto-population:', patient);
            
            if (patient.admission_date) {
              console.log('Raw admission_date:', patient.admission_date);
              // Format date to YYYY-MM-DDTHH:mm for datetime-local input
              const admissionDate = new Date(patient.admission_date);
              console.log('Parsed admission_date:', admissionDate);
              if (!isNaN(admissionDate.getTime())) {
                // Format for datetime-local input (YYYY-MM-DDTHH:mm)
                const formattedDate = admissionDate.toISOString().slice(0, 16);
                console.log('Formatted admission_date:', formattedDate);
                setValue('admission_date', formattedDate);
              }
            }
            
            if (patient.discharge_date) {
              console.log('Raw discharge_date:', patient.discharge_date);
              // Format date to YYYY-MM-DDTHH:mm for datetime-local input
              const dischargeDate = new Date(patient.discharge_date);
              console.log('Parsed discharge_date:', dischargeDate);
              if (!isNaN(dischargeDate.getTime())) {
                // Format for datetime-local input (YYYY-MM-DDTHH:mm)
                const formattedDate = dischargeDate.toISOString().slice(0, 16);
                console.log('Formatted discharge_date:', formattedDate);
                setValue('discharge_date', formattedDate);
              }
            }
            
            // Calculate length of stay if both dates are available
            if (patient.admission_date && patient.discharge_date) {
              const admit = new Date(patient.admission_date);
              const discharge = new Date(patient.discharge_date);
              if (!isNaN(admit.getTime()) && !isNaN(discharge.getTime())) {
                const lengthOfStay = Math.ceil((discharge.getTime() - admit.getTime()) / (1000 * 60 * 60 * 24));
                if (lengthOfStay >= 0) {
                  setValue('length_of_stay', lengthOfStay);
                }
              }
            }
            
            // Case-specific fields
            if (patient.case_type === 'mortality') {
              setValue('cause_of_death', patient.cause_of_death || '');
            } else if (patient.case_type === 'morbidity') {
              setValue('primary_diagnosis', patient.primary_diagnosis || patient.case_summary || '');
            }
          }
        }
      } else {
        // Handle API error response
        setError(auditResponse.message || 'Failed to load audit data');
        return;
      }
      
      // Fetch recommendation categories
      const categoriesResponse = await enhancedApi.recommendations.getCategories();
      if (categoriesResponse.success && 'data' in categoriesResponse) {
        setRecommendationCategories(categoriesResponse.data);
      }
      
      // Fetch departments
      const departmentsResponse = await enhancedApi.departments.getDepartments();
      if (departmentsResponse.success && 'data' in departmentsResponse) {
        setDepartments(departmentsResponse.data);
      }
      
      // Fetch units
      const unitsResponse = await enhancedApi.units.getUnits();
      if (unitsResponse.success && 'data' in unitsResponse) {
        setUnits(unitsResponse.data);
      }
      
    } catch (error: any) {
      console.error('Failed to fetch audit data:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const onSaveDraft = async (data: AuditFormData) => {
    try {
      setSaving(true);
      
      // Process form data to ensure boolean values are properly converted
      const processedData = {
        ...data,
        previous_admission_30_days: Boolean(data.previous_admission_30_days === true || data.previous_admission_30_days === 'true'),
        autopsy_needed: Boolean(data.autopsy_needed === true || data.autopsy_needed === 'true'),
        rca_needed: Boolean(data.rca_needed === true || data.rca_needed === 'true'),
        cpr_concern: Boolean(data.cpr_concern === true || data.cpr_concern === 'true'),
        mortality_unexpected: Boolean(data.mortality_unexpected === true || data.mortality_unexpected === 'true'),
        mortality_not_expected_admission: Boolean(data.mortality_not_expected_admission === true || data.mortality_not_expected_admission === 'true')
      };
      
      // For draft saving, we don't validate required fields
      // Just save whatever data is available
      
      // Capture signatures
      const teamWithSignatures = data.audit_team.map((member, index) => ({
        ...member,
        signature: signatureRefs.current[index]?.toDataURL() || ''
      }));

      const submitData = {
        ...processedData,
        audit_team: teamWithSignatures,
        status: 'draft' // Save as draft, not submitted
      };

      const auditResponse = await enhancedApi.audit.saveDraft(id!, submitData);
      
      if (auditResponse.success) {
        // Show success toast notification
        addToast({
          type: 'success',
          title: 'Draft Saved Successfully',
          message: 'Your audit draft has been saved. You can continue editing or submit when ready.',
        });
        
        // Update auto-save state
        setAutoSaveState(prev => ({
          ...prev,
          lastSaved: new Date(),
          isSaving: false,
          hasUnsavedChanges: false
        }));
        
        // Update progress if provided by backend
        if (auditResponse.data?.progress_percentage) {
          // Progress will be updated automatically by the form calculation
        }
        
        console.log('Draft saved successfully');
      } else {
        // Show error toast
        addToast({
          type: 'error',
          title: 'Failed to Save Draft',
          message: auditResponse.message || 'An error occurred while saving your draft. Please try again.',
        });
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      
      // Show error toast
      addToast({
        type: 'error',
        title: 'Failed to Save Draft',
        message: 'An unexpected error occurred. Please check your connection and try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (data: AuditFormData) => {
    try {
      setSaving(true);
      
      // Process form data to ensure boolean values are properly converted
      const processedData = {
        ...data,
        // Concatenate age and age_unit into a single string
        age: data.age + ' ' + data.age_unit,
        previous_admission_30_days: Boolean(data.previous_admission_30_days === true || data.previous_admission_30_days === 'true'),
        autopsy_needed: Boolean(data.autopsy_needed === true || data.autopsy_needed === 'true'),
        rca_needed: Boolean(data.rca_needed === true || data.rca_needed === 'true'),
        cpr_concern: Boolean(data.cpr_concern === true || data.cpr_concern === 'true'),
        mortality_unexpected: Boolean(data.mortality_unexpected === true || data.mortality_unexpected === 'true'),
        mortality_not_expected_admission: Boolean(data.mortality_not_expected_admission === true || data.mortality_not_expected_admission === 'true')
      };
      
      // Validate form
      const errors = validateForm(processedData);
      if (errors.length > 0) {
        setValidationErrors(errors);
        setSaving(false);
        return;
      }
      
      // Capture signatures
      const teamWithSignatures = data.audit_team.map((member, index) => ({
        ...member,
        signature: signatureRefs.current[index]?.toDataURL() || ''
      }));

      const submitData = {
        ...processedData,
        audit_team: teamWithSignatures,
        status: 'submitted'
      };

      // Save audit form
      const auditResponse = await enhancedApi.audit.submitAudit(id!, submitData);
      
      if (auditResponse.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to save audit:', error);
    } finally {
      setSaving(false);
    }
  };

  // Form validation
  const validateForm = (data: AuditFormData): string[] => {
    const errors: string[] = [];
    
    // Required fields validation
    if (!data.patient_ip_number) errors.push('Patient ID is required');
    if (!data.age) errors.push('Age is required');
    if (!data.primary_diagnosis) errors.push('Primary Diagnosis is required');
    if (!data.case_summary) errors.push('Comprehensive Case Summary is required');
    if (!data.issues_revealed) errors.push('Key Issues Identified During Audit is required');
    if (data.recommendations.length === 0) errors.push('At least one recommendation is required');
    if (data.audit_team.length === 0) errors.push('At least one team member is required');
    
    // Validate structured questions
    const questionSections = ['documentation_questions', 'diagnosis_questions', 'management_questions', 'outcome_questions', 'disclosure_questions', 'second_victim_questions'];
    questionSections.forEach(section => {
      const questions = data[section as keyof AuditFormData] as QuestionResponse[];
      questions.forEach((q, index) => {
        if (!q.answer) errors.push(`${section.replace('_', ' ')} question ${index + 1} requires an answer`);
        // Comments are optional, so no validation needed
      });
    });
    
    return errors;
  };

  const renderQuestionSection = (title: string, questions: any[], fieldName: keyof AuditFormData, sectionRef?: React.RefObject<HTMLDivElement | null>) => {
    return (
      <div ref={sectionRef} className="card">
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
        </div>
        
        <div className="space-y-4">
          {questions.map((question, index) => (
            <div key={question.id} className="border rounded-lg p-4">
              <div className="mb-3">
                <label className="form-label font-medium">{question.question}</label>
                <div className="flex gap-4 mt-2">
                  {question.options.map((option: string) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        value={option}
                        {...register(`${fieldName}.${index}.answer` as any)}
                        className="mr-2"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="form-label">Comments:</label>
                <textarea
                  {...register(`${fieldName}.${index}.comment` as any)}
                  className="form-textarea"
                  rows={3}
                  placeholder="Please provide detailed comments..."
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Section navigation component
  const renderSectionNavigation = () => (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Audit Form Progress</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {autoSaveState.lastSaved ? `Last saved: ${autoSaveState.lastSaved.toLocaleTimeString()}` : 'Not saved yet'}
            </span>
          </div>
          {autoSaveState.isSaving && (
            <div className="flex items-center space-x-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Auto-saving...</span>
            </div>
          )}
          {autoSaveState.hasUnsavedChanges && !autoSaveState.isSaving && (
            <div className="flex items-center space-x-2 text-orange-600">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-sm">Unsaved changes</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
          <span className="text-sm font-medium text-gray-700">{formProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${formProgress}%` }}
          ></div>
        </div>
      </div>
      
      {/* Section Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {formSections.map((section, index) => {
          const Icon = section.icon;
          const isActive = currentSection === index;
          const isCompleted = sectionCompletion[section.id] || false;
          
          return (
            <button
              key={section.id}
              onClick={() => {
                setCurrentSection(index);
                scrollToSection(index);
              }}
              className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                isActive 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : isCompleted 
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium text-center">{section.title}</span>
                {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                {section.required && !isCompleted && <AlertCircle className="h-4 w-4 text-red-500" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Enhanced recommendations section
  const renderRecommendationsSection = (sectionRef?: React.RefObject<HTMLDivElement | null>) => (
    <div ref={sectionRef} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <div className="w-6 h-6 bg-green-200 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-3 h-3 text-green-700" />
          </div>
          <span>Recommendations & Action Items</span>
        </h3>
      </div>
      
      <div className="p-6 space-y-6">
        {recommendationFields.map((field, index) => (
          <div key={field.id} className="border border-gray-200 rounded-xl p-6 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-gray-900">Recommendation {index + 1}</h4>
              <button
                type="button"
                onClick={() => removeRecommendation(index)}
                className="btn btn-sm btn-danger"
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title *</label>
                  <input
                    {...register(`recommendations.${index}.title`)}
                    className="form-input"
                    placeholder="Brief title for the recommendation"
                  />
                </div>
                
                <div>
                  <label className="form-label">Category *</label>
                  <select {...register(`recommendations.${index}.category_id`)} className="form-select">
                    <option value="">Select category</option>
                    {recommendationCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="form-label">Description *</label>
                <textarea
                  {...register(`recommendations.${index}.description`)}
                  className="form-textarea"
                  rows={3}
                  placeholder="Detailed description of the recommendation..."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Priority *</label>
                  <select {...register(`recommendations.${index}.priority`)} className="form-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Due Date *</label>
                  <input
                    type="date"
                    {...register(`recommendations.${index}.due_date`)}
                    className="form-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Action Plan</label>
                  <textarea
                    {...register(`recommendations.${index}.action_plan`)}
                    className="form-textarea"
                    rows={2}
                    placeholder="Specific steps to implement..."
                  />
                </div>
                
                <div>
                  <label className="form-label">Lead Responsible Person</label>
                  <input
                    {...register(`recommendations.${index}.lead_person`)}
                    className="form-input"
                    placeholder="Name and title"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={() => appendRecommendation({
            title: '',
            description: '',
            category_id: 1,
            priority: 'medium',
            due_date: '',
            action_plan: '',
            lead_person: ''
          })}
          className="btn btn-primary w-full"
        >
          <Plus className="h-4 w-4" />
          Add Recommendation
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Unable to Load Audit
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/audits')}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Back to My Audits
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* View-Only Banner for Approved Audits */}
      {isViewOnly && (
        <div className={`border-l-4 p-4 rounded-lg ${
          canEditApprovedAudit() 
            ? 'bg-blue-100 border-blue-500' 
            : 'bg-green-100 border-green-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className={`w-5 h-5 mr-2 ${
                canEditApprovedAudit() 
                  ? 'text-blue-600' 
                  : 'text-green-600'
              }`} />
              <p className={`font-medium ${
                canEditApprovedAudit() 
                  ? 'text-blue-800' 
                  : 'text-green-800'
              }`}>
                {canEditApprovedAudit() 
                  ? 'This audit has been approved but can be edited by reviewers and superusers'
                  : 'This audit has been approved and is now read-only'
                }
              </p>
            </div>
            <button
              onClick={() => navigate('/audits')}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
            >
              <FileText className="w-4 h-4" />
              <span>Return to Audits</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Mortality Audit Form</h1>
              <p className="text-blue-100">Comprehensive patient care review and analysis</p>
            </div>
          </div>
        </div>
        
        {patientData && (
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-700" />
                  </div>
                  <label className="text-sm font-semibold text-blue-700">Patient ID</label>
                </div>
                <p className="text-lg font-bold text-blue-900">{patientData.patient_id}</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-green-700" />
                  </div>
                  <label className="text-sm font-semibold text-green-700">Time of Death</label>
                </div>
                <p className="text-lg font-bold text-green-900">{patientData.time_of_death ? new Date(patientData.time_of_death).toLocaleDateString() : 'N/A'}</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-purple-700" />
                  </div>
                  <label className="text-sm font-semibold text-purple-700">Department</label>
                </div>
                <p className="text-lg font-bold text-purple-900">{patientData.dept}</p>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-indigo-200 rounded-lg flex items-center justify-center">
                    <Heart className="w-4 h-4 text-indigo-700" />
                  </div>
                  <label className="text-sm font-semibold text-indigo-700">Unit</label>
                </div>
                <p className="text-lg font-bold text-indigo-900">{patientData.unit_name}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Navigation */}
      {renderSectionNavigation()}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div ref={validationErrorsRef} className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-800">Please fix the following errors:</h3>
          </div>
          <ul className="list-disc list-inside space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index} className="text-red-700">{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={isViewOnly && !canEditApprovedAudit()} className={isViewOnly && !canEditApprovedAudit() ? 'opacity-75' : ''}>
        {/* Section 1: Case Information */}
        <div 
          ref={caseInfoRef}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-200 rounded-lg flex items-center justify-center">
                <FileText className="w-3 h-3 text-blue-700" />
              </div>
              <span>Case Information</span>
            </h3>
          </div>
          <div className="p-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Patient ID</label>
              <input
                {...register('patient_ip_number')}
                className="form-input"
                placeholder="Auto-populated from EMR"
              />
            </div>
            
            <div>
              <label className="form-label">Age</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  {...register('age', { required: true })}
                  className="form-input flex-1"
                  placeholder="Enter age"
                />
                <select
                  {...register('age_unit', { required: true })}
                  className="form-select"
                  defaultValue="years"
                >
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="form-label">Admission Date & Time *</label>
              <input
                type="datetime-local"
                {...register('admission_date', { required: true })}
                className="form-input"
              />
            </div>
            
            <div>
              <label className="form-label">Discharge Date & Time *</label>
              <input
                type="datetime-local"
                {...register('discharge_date', { required: true })}
                className="form-input"
              />
            </div>
            
            <div>
              <label className="form-label">Length of Stay (days)</label>
              <input
                type="number"
                value={
                  watch('discharge_date') && watch('admission_date') 
                    ? Math.ceil((new Date(watch('discharge_date')).getTime() - new Date(watch('admission_date')).getTime()) / (1000 * 3600 * 24))
                    : ''
                }
                disabled
                className="form-input bg-gray-50"
              />
            </div>
            
            <div>
              <label className="form-label">Source of Admission *</label>
              <select {...register('source_of_admission', { required: true })} className="form-select">
                <option value="">Select source</option>
                <option value="ER">ER</option>
                <option value="HD">HD</option>
                <option value="Clinic">Clinic</option>
                <option value="Wards">Wards</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Primary Diagnosis *</label>
              <textarea
                {...register('primary_diagnosis', { required: true })}
                className="form-textarea"
                rows={2}
                placeholder="ICD-10 code + description"
              />
            </div>
            
            
            <div>
              <label className="form-label">Previous Admission within 30 days</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('previous_admission_30_days') === 'true'}
                    onChange={() => setValue('previous_admission_30_days', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('previous_admission_30_days') === 'false'}
                    onChange={() => setValue('previous_admission_30_days', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
              </div>
            </div>
            
            
            <div>
              <label className="form-label">DNR Status</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input type="radio" value="Yes" {...register('dnr_status')} className="mr-2" />
                  Yes
                </label>
                <label className="flex items-center">
                  <input type="radio" value="No" {...register('dnr_status')} className="mr-2" />
                  No
                </label>
                <label className="flex items-center">
                  <input type="radio" value="Unknown" {...register('dnr_status')} className="mr-2" />
                  Unknown
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Cause of Death *</label>
              <textarea
                {...register('cause_of_death')}
                className="form-textarea"
                rows={2}
                placeholder="Free text + ICD-10 code"
              />
            </div>
            
            <div>
              <label className="form-label">Autopsy Needed</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('autopsy_needed') === 'true'}
                    onChange={() => setValue('autopsy_needed', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('autopsy_needed') === 'false'}
                    onChange={() => setValue('autopsy_needed', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Autopsy Justification</label>
              <textarea
                {...register('autopsy_justification')}
                className="form-textarea"
                rows={2}
                placeholder="Justification text"
              />
            </div>
            
            <div>
              <label className="form-label">RCA needed</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('rca_needed') === 'true'}
                    onChange={() => setValue('rca_needed', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('rca_needed') === 'false'}
                    onChange={() => setValue('rca_needed', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">RCA Comment</label>
              <textarea
                {...register('rca_comment')}
                className="form-textarea"
                rows={2}
                placeholder="RCA comment"
              />
            </div>
            
            <div>
              <label className="form-label">Any concern regarding CPR</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('cpr_concern') === 'true'}
                    onChange={() => setValue('cpr_concern', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('cpr_concern') === 'false'}
                    onChange={() => setValue('cpr_concern', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">CPR Comment</label>
              <textarea
                {...register('cpr_comment')}
                className="form-textarea"
                rows={2}
                placeholder="CPR comment"
              />
            </div>
            
            <div>
              <label className="form-label">Mortality was unexpected</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('mortality_unexpected') === 'true'}
                    onChange={() => setValue('mortality_unexpected', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('mortality_unexpected') === 'false'}
                    onChange={() => setValue('mortality_unexpected', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="na" 
                    checked={watch('mortality_unexpected') === 'na'}
                    onChange={() => setValue('mortality_unexpected', 'na')}
                    className="mr-2" 
                  />
                  N/A
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Mortality Unexpected Comment</label>
              <textarea
                {...register('mortality_unexpected_comment')}
                className="form-textarea"
                rows={2}
                placeholder="Mortality unexpected comment"
              />
            </div>
            
            <div>
              <label className="form-label">Mortality not expected at the time of admission but expected at time of death</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="true" 
                    checked={watch('mortality_not_expected_admission') === 'true'}
                    onChange={() => setValue('mortality_not_expected_admission', 'true')}
                    className="mr-2" 
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="false" 
                    checked={watch('mortality_not_expected_admission') === 'false'}
                    onChange={() => setValue('mortality_not_expected_admission', 'false')}
                    className="mr-2" 
                  />
                  No
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    value="na" 
                    checked={watch('mortality_not_expected_admission') === 'na'}
                    onChange={() => setValue('mortality_not_expected_admission', 'na')}
                    className="mr-2" 
                  />
                  N/A
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Mortality Not Expected at Admission Comment</label>
              <textarea
                {...register('mortality_not_expected_admission_comment')}
                className="form-textarea"
                rows={2}
                placeholder="Mortality not expected at admission comment"
              />
            </div>
          </div>
          </div>
        </div>

        {/* Section 2: Case Summary */}
        <div 
          ref={caseSummaryRef}
          className="card"
        >
          <div className="card-header">
            <h3 className="card-title">Case Summary</h3>
          </div>
          
          <div>
            <label className="form-label">Comprehensive Case Summary *</label>
            <textarea
              {...register('case_summary')}
              className="form-textarea"
              rows={8}
              placeholder="Provide a comprehensive summary of the case, including key clinical events, treatment decisions, and circumstances leading to mortality..."
            />
          </div>
        </div>

        {/* Section 3: Structured Audit Questions */}
        {renderQuestionSection('Documentation Adequacy', questionSets.documentation, 'documentation_questions', documentationRef)}
        {renderQuestionSection('Diagnosis Adequacy', questionSets.diagnosis, 'diagnosis_questions', diagnosisRef)}
        {renderQuestionSection('Management Process', questionSets.management, 'management_questions', managementRef)}
        {renderQuestionSection('Outcome Contributors', questionSets.outcome, 'outcome_questions', outcomeRef)}
        {renderQuestionSection('Disclosure Section', questionSets.disclosure, 'disclosure_questions', disclosureRef)}
        {renderQuestionSection('Second Victim Support', questionSets.second_victim, 'second_victim_questions', secondVictimRef)}

        {/* Section 4: Issues Revealed */}
        <div 
          ref={issuesRef}
          className="card"
        >
          <div className="card-header">
            <h3 className="card-title">Issues Revealed</h3>
          </div>
          
          <div>
            <label className="form-label">Key Issues Identified During Audit *</label>
            <textarea
              {...register('issues_revealed')}
              className="form-textarea"
              rows={6}
              placeholder="List and describe significant issues, gaps, or concerns identified during the mortality review process..."
            />
          </div>
        </div>

        {/* Section 5: Enhanced Recommendations */}
        {renderRecommendationsSection(recommendationsRef)}

        {/* Section 6: Audit Team Information */}
        <div 
          ref={teamRef}
          className="card"
        >
          <div className="card-header">
            <h3 className="card-title">Audit Team & Signatures</h3>
          </div>
          
          <div className="space-y-4">
            {teamFields.map((field, index) => (
              <div key={field.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium">Team Member {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeTeamMember(index)}
                    className="btn btn-sm btn-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Name</label>
                      <input
                        {...register(`audit_team.${index}.name`)}
                        className="form-input"
                        placeholder="Full name"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Role/Title</label>
                      <input
                        {...register(`audit_team.${index}.role`)}
                        className="form-input"
                        placeholder="Position/specialty"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Department</label>
                      <select
                        {...register(`audit_team.${index}.department`)}
                        className="form-select"
                      >
                        <option value="">Select Department</option>
                        {departments.filter(d => d.is_active).map(dept => (
                          <option key={dept.id} value={dept.name}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Electronic Signature</label>
                      <div className="border rounded-lg p-4">
                        <CustomSignatureCanvas
                          ref={(ref) => {
                            signatureRefs.current[index] = ref;
                          }}
                          onBegin={() => handleSignatureStart(index)}
                          onEnd={() => handleSignatureEnd(index)}
                          width={400}
                          height={150}
                          className="border rounded"
                          defaultSignature={watch(`audit_team.${index}.signature`)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            (signatureRefs.current[index] as any)?.clear();
                            handleSignatureEnd(index);
                          }}
                          className="btn btn-sm btn-outline mt-2"
                        >
                          Clear Signature
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="form-label">Date & Time</label>
                      <input
                        type="datetime-local"
                        {...register(`audit_team.${index}.signature_date`)}
                        className="form-input"
                        defaultValue={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => appendTeamMember({
                name: '',
                role: '',
                department: '',
                signature: '',
                signature_date: new Date().toISOString().slice(0, 16)
              })}
              className="btn btn-secondary"
            >
              <Plus className="h-4 w-4" />
              Add Team Member
            </button>
          </div>
        </div>

        {/* Enhanced Form Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          {(!isViewOnly || canEditApprovedAudit()) && (
            <div className="flex justify-center">
              {/* Save and Submit Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => onSaveDraft(getValues())}
                  disabled={saving}
                  className="px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5" />
                      <span>Save Draft</span>
                    </>
                  )}
                </button>
                
                <button
                  type="submit"
                  disabled={saving}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>Submit Audit</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        </fieldset>
      </form>
    </div>
  );
};
